import {InfoOutlined, LocalOfferOutlined} from "@mui/icons-material";
import {Box, IconButton, Stack, Tooltip, Typography} from "@mui/material";
import {alpha, useTheme} from "@mui/material/styles";
import {FC, MouseEvent} from "react";
import {useTranslation} from "react-i18next";

interface Props {
    cost: number
    originalCost: number | null
    drawCount?: number
    drawNames?: string[]
    color: string
}

const BundleDealBadge: FC<Props> = ({cost, originalCost, drawCount = 0, drawNames = [], color}) => {
    const theme = useTheme()
    const {t} = useTranslation()
    const hasDiscount = !!originalCost && originalCost > cost
    const discountPercent = hasDiscount ? Math.floor(((originalCost - cost) / originalCost) * 100) : 0
    const contrastText = theme.palette.tertiary.contrastText ?? theme.palette.primary.contrastText

    const handleInfoClick = (event: MouseEvent<HTMLElement>) => {
        event.preventDefault()
        event.stopPropagation()
    }

    const tooltipTitle = (
        <Box sx={{maxWidth: 280}}>
            <Typography variant="caption" sx={{display: "block", fontWeight: 700, mb: 0.75}}>
                {t("bundle_tooltip_explainer")}
            </Typography>
            <Stack spacing={0.35}>
                {drawNames.map((name, index) => (
                    <Typography key={`${name}-${index}`} variant="caption" sx={{display: "block", lineHeight: 1.35}}>
                        • {name}
                    </Typography>
                ))}
            </Stack>
        </Box>
    )

    const includedDrawsText = (
        <Typography
            component="span"
            variant="caption"
            textAlign="center"
            sx={{
                color: alpha(color, 0.82),
                lineHeight: 1.1,
                borderBottom: `1px dotted ${alpha(color, 0.55)}`,
            }}
        >
            {t("bundle_included_draws_count", {count: drawCount})}
        </Typography>
    )

    return (
        <Stack alignItems="center" spacing={0.5} mt={0.85}>
            <Box
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.45,
                    maxWidth: "100%",
                    px: 0.9,
                    py: 0.4,
                    borderRadius: "999px",
                    color: contrastText,
                    backgroundColor: theme.palette.tertiary.main,
                    border: `1px solid ${alpha(contrastText, 0.3)}`,
                    boxShadow: `0 2px 8px ${alpha("#000", 0.22)}`,
                }}
            >
                <LocalOfferOutlined sx={{fontSize: 15, flexShrink: 0}}/>
                <Typography
                    variant="caption"
                    fontWeight={800}
                    noWrap
                    sx={{lineHeight: 1.1}}
                >
                    {hasDiscount
                        ? `${t("bundle_deal")} · ${t("save_percent", {percent: discountPercent})}`
                        : t("bundle_deal")}
                </Typography>
            </Box>
            {drawCount > 0 && (
                drawNames.length > 0 ? (
                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.25}>
                        {includedDrawsText}
                        <Tooltip
                            title={tooltipTitle}
                            arrow
                            placement="top"
                            enterTouchDelay={500}
                            leaveTouchDelay={2500}
                        >
                            <IconButton
                                size="small"
                                aria-label={t("included_draws")}
                                onClick={handleInfoClick}
                                sx={{
                                    width: 20,
                                    height: 20,
                                    p: 0,
                                    color: alpha(color, 0.82),
                                    "&:hover": {
                                        backgroundColor: alpha(color, 0.08),
                                    },
                                }}
                            >
                                <InfoOutlined sx={{fontSize: 15}}/>
                            </IconButton>
                        </Tooltip>
                    </Stack>
                ) : (
                    includedDrawsText
                )
            )}
        </Stack>
    )
}

export default BundleDealBadge;
